import { inject, Injectable } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { AuthUtils } from '../utils/auth-util';
import {
  CacheService,
  LocalStorageEnum,
  SpVersionService,
  StorageService,
} from '../services';
import { IBaseResponse } from '../model/base-response';
import { ONEID_URL } from './pages/one-id-callback/api_url';
import { BasePublicHttpService } from '../services/base.public.service';
import { SignalRConnectionState, SignalRService } from './signalr.service';
import { CLIENT_TYPE } from '../utils';

export interface ITokenBaseModel extends IBaseResponse {
  result?: ITokenModel;
}
export interface ITokenModel {
  accessToken: string;
  refreshToken: string;
  expiration: Date;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _authenticated = false;

  private $baseHttpService = inject(BasePublicHttpService);

  private $storageService = inject(StorageService);

  private $cacheService = inject(CacheService);

  private $spVersionService = inject(SpVersionService);

  private _signalRService = inject(SignalRService); // SignalR servisni inject qilish
  private _destroy$ = new Subject<void>();

  $client_type = inject(CLIENT_TYPE);

  set accessToken(token: string) {
    if (token) {
      this.$storageService.set(LocalStorageEnum.AccessToken, token);
    }
  }

  get accessToken(): string {
    return this.$storageService.get(LocalStorageEnum.AccessToken) ?? '';
  }

  set refreshToken(token: string) {
    if (token) {
      this.$storageService.set(LocalStorageEnum.RefreshToken, token);
    }
  }

  get refreshToken(): string {
    return this.$storageService.get(LocalStorageEnum.RefreshToken) ?? '';
  }

  forgotPassword(email: string, url: string): Observable<any> {
    return this.$baseHttpService.post('auth/forgot-password', email);
  }

  resetPassword(
    data: any,
    url?: string,
    recaptchaToken?: string,
  ): Observable<any> {
    const token =
      recaptchaToken ??
      (typeof window !== 'undefined'
        ? (window as any).grecaptcha?.getResponse?.()
        : '');
    const headers: Record<string, string> = {
      'X-Recaptcha-Token': token || '',
    };
    return this.$baseHttpService
      .post('auth/reset-password', data, { headers })
      .pipe(
        finalize(() => {
          if (typeof window !== 'undefined' && (window as any).grecaptcha?.reset) {
            try {
              (window as any).grecaptcha.reset();
            } catch {
              // ignore
            }
          }
        }),
      );
  }

  oneIdCallback(code: string, url: string) {
    return this.$baseHttpService.get<ITokenBaseModel>(ONEID_URL.ONE_ID + code);
  }

  signIn(
    credentials: { login: string; password: string },
    url?: string,
    recaptchaToken?: string,
  ): Observable<ITokenModel | undefined> {
    const token =
      recaptchaToken ??
      (typeof window !== 'undefined'
        ? (window as any).grecaptcha?.getResponse?.()
        : '');
    const headers: Record<string, string> = {
      'X-Recaptcha-Token': token || '',
    };
    return this.$baseHttpService
      .post<ITokenBaseModel>('auth/login', credentials, { headers })
      .pipe(
        finalize(() => {
          if (typeof window !== 'undefined' && (window as any).grecaptcha?.reset) {
            try {
              (window as any).grecaptcha.reset();
            } catch {
              // ignore
            }
          }
        }),
        switchMap((response: ITokenBaseModel) => {
          if (response?.result) {
            this.setToken(response);
          }
          return of(response.result);
        }),
      );
  }

  setToken(response: ITokenBaseModel) {
    this.accessToken = response?.result?.accessToken ?? '';
    this.refreshToken = response?.result?.refreshToken ?? '';
    this._authenticated = true;

    if (this.$client_type == 'admin_cabinet') {
      console.log('AccessToken: ', this.accessToken);
      if (this.accessToken) {
        this._signalRService.startConnection();
      }

      this._signalRService.connectionState$
        .pipe(takeUntil(this._destroy$))
        .subscribe((state) => {
          if (state === SignalRConnectionState.Connected) {
            console.log('Tizim markaziy xabarlar serveriga ulandi.');
          } else if (state === SignalRConnectionState.Reconnecting) {
            console.warn('Aloqa uzildi. Qayta ulanishga urinilmoqda...');
          }
        });
    }
  }

  logOutUser() {
    return this.$baseHttpService.post('auth/logout', {});
  }

  signOut(): Observable<boolean> {
    return this.logOutUser().pipe(
      catchError((err) => {
        console.error("Logout so'rovida xatolik", err);
        return of(undefined);
      }),
      map(() => true),
      finalize(() => {
        this._authenticated = false;
        this._signalRService.stopConnection();
        this.$storageService.clear();
        void this.$cacheService.clear();
        this.$spVersionService.reset();
        this._destroy$.next();
        this._destroy$.complete();
      }),
    );
  }

  check(): Observable<boolean> {
    if (this._authenticated) {
      return of(true);
    }
    if (!this.accessToken) {
      return of(false);
    }
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return of(false);
    }
    return of(true);
  }
}
